import { useEffect, useId, useMemo, useRef, useState, type ReactElement } from 'react';
import {
  AccountBalanceWalletOutlined,
  AccountTreeOutlined,
  AddOutlined,
  ArrowDownwardOutlined,
  ArrowUpwardOutlined,
  CalendarMonthOutlined,
  ChevronRightOutlined,
  ContentPasteOutlined,
  DeleteOutlineOutlined,
  DescriptionOutlined,
  EditOutlined,
  EventAvailableOutlined,
  ExpandMoreOutlined,
  FactCheckOutlined,
  AttachFileOutlined,
  LinkOutlined,
  PersonOutlined,
  RuleOutlined,
  TaskAltOutlined,
} from '@mui/icons-material';
import {
  Alert,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  ButtonBase,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import type { WorkflowFieldDefinition, WorkflowFieldType } from './model';
import { AnchoredPopover, ChoiceControl, createFocusRingStyles, designTokens, FormTextField, HelpPopover, ValueBadge } from '../../design-system';

type WorkflowFieldTypeOption = {
  value: WorkflowFieldType;
  label: string;
  description: string;
  group: '입력' | '선택·참조' | '안내';
  defaultLabel: string;
  defaultKey: string;
  icon: ReactElement;
};

const workflowFieldTypes: WorkflowFieldTypeOption[] = [
  { value: 'SHORT_TEXT', label: '짧은 텍스트', description: '이름이나 사유처럼 한 줄로 답합니다.', group: '입력', defaultLabel: '짧은 답변', defaultKey: 'short_text', icon: <EditOutlined /> },
  { value: 'LONG_TEXT', label: '긴 텍스트', description: '설명이나 요청 내용을 여러 줄로 답합니다.', group: '입력', defaultLabel: '상세 내용', defaultKey: 'long_text', icon: <DescriptionOutlined /> },
  { value: 'NUMBER', label: '숫자', description: '수량이나 인원처럼 숫자만 입력합니다.', group: '입력', defaultLabel: '숫자', defaultKey: 'number', icon: <RuleOutlined /> },
  { value: 'CURRENCY', label: '금액', description: '비용이나 예산처럼 금액을 입력합니다.', group: '입력', defaultLabel: '금액', defaultKey: 'amount', icon: <AccountBalanceWalletOutlined /> },
  { value: 'DATE', label: '날짜', description: '하나의 날짜를 선택합니다.', group: '입력', defaultLabel: '날짜', defaultKey: 'date', icon: <CalendarMonthOutlined /> },
  { value: 'DATE_RANGE', label: '기간', description: '시작일과 종료일을 함께 선택합니다.', group: '입력', defaultLabel: '기간', defaultKey: 'date_range', icon: <EventAvailableOutlined /> },
  { value: 'SINGLE_SELECT', label: '단일 선택', description: '미리 정한 항목 중 하나를 고릅니다.', group: '선택·참조', defaultLabel: '선택', defaultKey: 'single_select', icon: <TaskAltOutlined /> },
  { value: 'MULTI_SELECT', label: '복수 선택', description: '미리 정한 항목을 여러 개 고릅니다.', group: '선택·참조', defaultLabel: '복수 선택', defaultKey: 'multi_select', icon: <FactCheckOutlined /> },
  { value: 'PERSON', label: '구성원', description: '구성원을 이름으로 검색해 선택합니다.', group: '선택·참조', defaultLabel: '구성원', defaultKey: 'person', icon: <PersonOutlined /> },
  { value: 'ORGANIZATION', label: '조직', description: '조직을 이름으로 검색해 선택합니다.', group: '선택·참조', defaultLabel: '조직', defaultKey: 'organization', icon: <AccountTreeOutlined /> },
  { value: 'FILE', label: '파일', description: '파일을 여러 개 끌어다 놓거나 선택해 첨부합니다.', group: '입력', defaultLabel: '첨부 파일', defaultKey: 'files', icon: <AttachFileOutlined /> },
  { value: 'WORKFLOW_REFERENCE', label: '다른 문서 참조', description: '열람 가능한 다른 워크플로우 문서를 여러 개 검색해 연결합니다.', group: '선택·참조', defaultLabel: '참조 문서', defaultKey: 'reference', icon: <LinkOutlined /> },
  { value: 'DISPLAY_TEXT', label: '안내 문구', description: '입력 없이 설명이나 유의사항을 보여줍니다.', group: '안내', defaultLabel: '안내 문구', defaultKey: 'information', icon: <DescriptionOutlined /> },
];

const fieldKeyPattern = /^[a-z][a-z0-9_]{0,63}$/;

export function workflowFieldTypeLabel(value: WorkflowFieldType): string {
  return workflowFieldTypes.find((type) => type.value === value)?.label ?? value;
}

export function workflowFieldsAreValid(fields: WorkflowFieldDefinition[]): boolean {
  if (fields.length === 0) return false;
  const keys = new Set<string>();
  return fields.every((field, index) => {
    if (field.order !== index + 1 || !field.label.trim() || !fieldKeyPattern.test(field.key) || keys.has(field.key)) return false;
    keys.add(field.key);
    if (field.searchable && field.sensitivity !== 'INTERNAL') return false;
    if (field.type === 'DISPLAY_TEXT' && field.required) return false;
    if (field.type === 'SINGLE_SELECT' || field.type === 'MULTI_SELECT') {
      const options = fieldOptions(field);
      const values = new Set<string>();
      const labels = new Set<string>();
      if (options.length === 0) return false;
      for (const option of options) {
        const label = (option.label ?? '').trim().toLocaleLowerCase('ko-KR');
        if (!option.value.trim() || !label || values.has(option.value) || labels.has(label)) return false;
        values.add(option.value);
        labels.add(label);
      }
    }
    if (field.type === 'SHORT_TEXT' || field.type === 'LONG_TEXT') {
      const maximum = field.validation?.maxLength;
      if (maximum !== undefined && (!Number.isInteger(maximum) || Number(maximum) < 1)) return false;
    }
    if (field.type === 'NUMBER' || field.type === 'CURRENCY') {
      const minimum = field.validation?.min;
      const maximum = field.validation?.max;
      if (minimum !== undefined && !isFiniteNumber(minimum)) return false;
      if (maximum !== undefined && !isFiniteNumber(maximum)) return false;
      if (isFiniteNumber(minimum) && isFiniteNumber(maximum) && minimum > maximum) return false;
    }
    if (field.type === 'FILE') {
      const maximum = field.config?.maxFiles ?? 10;
      if (!Number.isInteger(maximum) || Number(maximum) < 1 || Number(maximum) > 20) return false;
    }
    return true;
  });
}

type WorkflowFieldsEditorProps = {
  fields: WorkflowFieldDefinition[];
  referencedFieldKeys: ReadonlySet<string>;
  onChange: (fields: WorkflowFieldDefinition[]) => void;
  onFieldKeyChange: (previousKey: string, nextKey: string) => void;
};

export function WorkflowFieldsEditor({ fields, referencedFieldKeys, onChange, onFieldKeyChange }: WorkflowFieldsEditorProps) {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [typePickerAnchor, setTypePickerAnchor] = useState<HTMLElement | null>(null);
  const typePickerTitleId = useId();
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down('md'));
  const inspectorRef = useRef<HTMLDivElement>(null);
  const focusInspectorAfterSelection = useRef(false);
  const fieldRowKeys = useRef(new WeakMap<WorkflowFieldDefinition, string>());
  const nextFieldRowKey = useRef(1);
  const selectedField = fields[selectedIndex];

  const fieldRowKey = (field: WorkflowFieldDefinition) => {
    const current = fieldRowKeys.current.get(field);
    if (current) return current;
    const next = `workflow-field-${nextFieldRowKey.current++}`;
    fieldRowKeys.current.set(field, next);
    return next;
  };

  const selectForEditing = (index: number) => {
    focusInspectorAfterSelection.current = mobile;
    if (mobile && index === selectedIndex) {
      focusInspectorAfterSelection.current = false;
      window.requestAnimationFrame(() => {
        inspectorRef.current?.scrollIntoView({ block: 'start' });
        inspectorRef.current?.focus({ preventScroll: true });
      });
    }
    setSelectedIndex(index);
  };

  const preserveFieldRowKeys = (source: WorkflowFieldDefinition[], next: WorkflowFieldDefinition[]) => {
    next.forEach((field, index) => {
      const sourceField = source[index];
      if (sourceField) fieldRowKeys.current.set(field, fieldRowKey(sourceField));
    });
    return next;
  };

  useEffect(() => {
    if (selectedIndex >= fields.length) setSelectedIndex(Math.max(0, fields.length - 1));
  }, [fields.length, selectedIndex]);

  useEffect(() => {
    if (!mobile || !focusInspectorAfterSelection.current) return;
    focusInspectorAfterSelection.current = false;
    const frame = window.requestAnimationFrame(() => {
      inspectorRef.current?.scrollIntoView({ block: 'start' });
      inspectorRef.current?.focus({ preventScroll: true });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [fields.length, mobile, selectedIndex]);

  const updateField = (patch: Partial<WorkflowFieldDefinition>) => {
    if (!selectedField) return;
    const next = fields.map((field, index) => {
      if (index !== selectedIndex) return field;
      const updated = { ...field, ...patch };
      fieldRowKeys.current.set(updated, fieldRowKey(field));
      return updated;
    });
    onChange(next);
  };

  const changeFieldKey = (nextKey: string) => {
    if (!selectedField) return;
    const previousKey = selectedField.key;
    updateField({ key: nextKey });
    if (previousKey !== nextKey) onFieldKeyChange(previousKey, nextKey);
  };

  const selectFieldType = (type: WorkflowFieldType) => {
    const next = [...fields, createField(fields, type)];
    onChange(next);
    selectForEditing(next.length - 1);
    setTypePickerAnchor(null);
  };

  const changeSelectedFieldType = (type: WorkflowFieldType) => {
    if (!selectedField || (referencedFieldKeys.has(selectedField.key) && type !== 'PERSON')) return;
    updateField(normalizeFieldType(selectedField, type));
  };

  const moveField = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= fields.length) return;
    const source = [...fields];
    [source[index], source[target]] = [source[target], source[index]];
    onChange(preserveFieldRowKeys(source, reorderFields(source)));
    if (selectedIndex === index) setSelectedIndex(target);
    else if (selectedIndex === target) setSelectedIndex(index);
  };

  const deleteSelectedField = () => {
    if (!selectedField || referencedFieldKeys.has(selectedField.key)) return;
    const source = fields.filter((_, index) => index !== selectedIndex);
    const next = preserveFieldRowKeys(source, reorderFields(source));
    onChange(next);
    setSelectedIndex(Math.min(selectedIndex, Math.max(0, next.length - 1)));
  };

  const duplicateSelectedField = () => {
    if (!selectedField) return;
    const duplicate: WorkflowFieldDefinition = {
      ...selectedField,
      id: undefined,
      key: uniqueFieldKey(fields, `${selectedField.key}_copy`),
      order: selectedIndex + 2,
      label: `${selectedField.label} 복사본`,
      config: selectedField.config ? structuredClone(selectedField.config) : undefined,
      validation: selectedField.validation ? structuredClone(selectedField.validation) : undefined,
    };
    const source = [...fields.slice(0, selectedIndex + 1), duplicate, ...fields.slice(selectedIndex + 1)];
    const next = preserveFieldRowKeys(source, reorderFields(source));
    onChange(next);
    selectForEditing(selectedIndex + 1);
  };

  return (
    <Paper variant="outlined" sx={{ minWidth: 0, overflow: 'hidden', borderRadius: designTokens.radius.surface }}>
      <Stack direction="row" sx={{ minHeight: 56, px: { xs: 1.5, sm: 2 }, alignItems: 'center', gap: 1 }}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography component="h2" variant="h6">양식 필드</Typography>
        </Box>
        <Button
          size="small"
          variant="outlined"
          startIcon={<AddOutlined />}
          aria-haspopup="dialog"
          aria-expanded={Boolean(typePickerAnchor)}
          onClick={(event) => setTypePickerAnchor(event.currentTarget)}
        >
          필드 추가
        </Button>
      </Stack>
      <Divider />
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 1.15fr) minmax(300px, 0.85fr)' }, minWidth: 0 }}>
        <Box sx={{ p: { xs: 1.25, sm: 2 }, minWidth: 0 }}>
          <Stack direction="row" sx={{ alignItems: 'center', mb: 1.25, gap: 1 }}>
            <Typography variant="subtitle2" sx={{ flex: 1 }}>요청 화면 구성</Typography>
            <Typography variant="caption" color="text.secondary">{fields.length}개 필드</Typography>
          </Stack>
          {fields.length === 0 ? (
            <Alert severity="warning" action={<Button color="inherit" size="small" onClick={(event) => setTypePickerAnchor(event.currentTarget)}>필드 추가</Button>}>
              요청을 받으려면 필드가 하나 이상 필요합니다.
            </Alert>
          ) : (
            <Stack role="list" aria-label="양식 필드" spacing={0.75}>
              {fields.map((field, index) => {
                const selected = index === selectedIndex;
                const referenced = referencedFieldKeys.has(field.key);
                return (
                  <Paper
                    key={field.id ?? fieldRowKey(field)}
                    role="listitem"
                    variant="outlined"
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: 'minmax(0, 1fr) auto',
                      alignItems: 'center',
                      minWidth: 0,
                      overflow: 'hidden',
                      borderColor: selected ? designTokens.color.action : designTokens.color.divider,
                      bgcolor: selected ? designTokens.color.actionSoft : designTokens.color.canvas,
                      boxShadow: selected ? `0 0 0 2px ${designTokens.color.brandBorder}` : 'none',
                    }}
                  >
                    <ButtonBase
                      aria-label={`${field.label || '이름 없는 필드'} 속성 편집`}
                      aria-pressed={selected}
                      onClick={() => selectForEditing(index)}
                      sx={{
                        minWidth: 0,
                        width: '100%',
                        px: 1.25,
                        py: 1.15,
                        display: 'grid',
                        gridTemplateColumns: '36px minmax(0, 1fr) auto',
                        gap: 1.1,
                        alignItems: 'center',
                        textAlign: 'left',
                        '&.Mui-focusVisible': createFocusRingStyles({ inset: true }),
                      }}
                    >
                      <FieldTypeIcon type={field.type} selected={selected} />
                      <Box sx={{ minWidth: 0 }}>
                        <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center', minWidth: 0 }}>
                          <Typography variant="body2" sx={{ minWidth: 0, fontWeight: 700 }} noWrap>{field.label || '필드 이름을 입력하세요'}</Typography>
                          {field.required && <Typography component="span" color="error" aria-label="필수">*</Typography>}
                        </Stack>
                        <Stack direction="row" spacing={0.5} useFlexGap sx={{ mt: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
                          <ValueBadge size="compact" label={workflowFieldTypeLabel(field.type)} />
                          {field.required ? <ValueBadge size="compact" label="필수" /> : null}
                          {field.sensitivity !== 'INTERNAL' ? <ValueBadge size="compact" label={sensitivityLabel(field.sensitivity)} /> : null}
                          {referenced ? <ValueBadge size="compact" label="결재선에서 사용 중" /> : null}
                          <Typography variant="caption" color="text.secondary" noWrap>{field.key || '필드 키 없음'}</Typography>
                        </Stack>
                      </Box>
                      <ChevronRightOutlined color={selected ? 'primary' : 'action'} fontSize="small" />
                    </ButtonBase>
                    <Stack direction="row" sx={{ pr: 0.5 }}>
                      <Tooltip title="위로 이동">
                        <span><IconButton size="small" aria-label={`${field.label} 필드 위로 이동`} disabled={index === 0} onClick={() => moveField(index, -1)}><ArrowUpwardOutlined fontSize="small" /></IconButton></span>
                      </Tooltip>
                      <Tooltip title="아래로 이동">
                        <span><IconButton size="small" aria-label={`${field.label} 필드 아래로 이동`} disabled={index === fields.length - 1} onClick={() => moveField(index, 1)}><ArrowDownwardOutlined fontSize="small" /></IconButton></span>
                      </Tooltip>
                    </Stack>
                  </Paper>
                );
              })}
            </Stack>
          )}
          <Button
            fullWidth
            startIcon={<AddOutlined />}
            aria-haspopup="dialog"
            aria-expanded={Boolean(typePickerAnchor)}
            onClick={(event) => setTypePickerAnchor(event.currentTarget)}
            sx={{ mt: 1, justifyContent: 'flex-start', border: 1, borderStyle: 'dashed', borderColor: designTokens.color.strongDivider, color: designTokens.color.secondaryText }}
          >
            필드 추가
          </Button>
        </Box>

        <Box ref={inspectorRef} tabIndex={-1} aria-label="선택한 필드 속성" sx={{ minWidth: 0, scrollMarginTop: 12, outline: 'none', borderTop: { xs: `1px solid ${designTokens.color.divider}`, md: 0 }, borderLeft: { xs: 0, md: `1px solid ${designTokens.color.divider}` }, bgcolor: designTokens.color.subtleSurface }}>
          <Box sx={{ p: { xs: 1.5, sm: 2 }, position: { md: 'sticky' }, top: { md: 16 } }}>
            {selectedField ? (
              <FieldInspector
                field={selectedField}
                fields={fields}
                referenced={referencedFieldKeys.has(selectedField.key)}
                onChange={updateField}
                onKeyChange={changeFieldKey}
                onTypeChange={changeSelectedFieldType}
                onDuplicate={duplicateSelectedField}
                onDelete={deleteSelectedField}
              />
            ) : (
              <Stack spacing={1} sx={{ py: 4, textAlign: 'center' }}>
                <Typography variant="subtitle2">편집할 필드를 선택하세요.</Typography>
                <Typography variant="body2" color="text.secondary">왼쪽에서 필드를 추가하거나 선택하면 속성이 여기에 표시됩니다.</Typography>
              </Stack>
            )}
          </Box>
        </Box>
      </Box>

      <FieldTypePicker anchor={typePickerAnchor} titleId={typePickerTitleId} onClose={() => setTypePickerAnchor(null)} onSelect={selectFieldType} />
    </Paper>
  );
}

function FieldInspector({ field, fields, referenced, onChange, onKeyChange, onTypeChange, onDuplicate, onDelete }: {
  field: WorkflowFieldDefinition;
  fields: WorkflowFieldDefinition[];
  referenced: boolean;
  onChange: (patch: Partial<WorkflowFieldDefinition>) => void;
  onKeyChange: (nextKey: string) => void;
  onTypeChange: (type: WorkflowFieldType) => void;
  onDuplicate: () => void;
  onDelete: () => void;
}) {
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const duplicateKey = fields.filter((candidate) => candidate.key === field.key).length > 1;
  const invalidKey = !fieldKeyPattern.test(field.key);
  const options = fieldOptions(field);
  const choiceField = field.type === 'SINGLE_SELECT' || field.type === 'MULTI_SELECT';
  const displayField = field.type === 'DISPLAY_TEXT';
  const textField = field.type === 'SHORT_TEXT' || field.type === 'LONG_TEXT';
  const numberField = field.type === 'NUMBER' || field.type === 'CURRENCY';
  const fileField = field.type === 'FILE';
  const maximumLength = field.validation?.maxLength;
  const minimum = field.validation?.min;
  const maximum = field.validation?.max;
  const invalidRange = isFiniteNumber(minimum) && isFiniteNumber(maximum) && minimum > maximum;
  const optionValueCounts = options.reduce((counts, option) => counts.set(option.value, (counts.get(option.value) ?? 0) + 1), new Map<string, number>());
  const optionLabelCounts = options.reduce((counts, option) => {
    const label = (option.label ?? '').trim().toLocaleLowerCase('ko-KR');
    return counts.set(label, (counts.get(label) ?? 0) + 1);
  }, new Map<string, number>());
  const optionRowKeys = useRef(new WeakMap<object, string>());
  const nextOptionRowKey = useRef(1);
  const optionRowKey = (option: object) => {
    const current = optionRowKeys.current.get(option);
    if (current) return current;
    const next = `workflow-option-${nextOptionRowKey.current++}`;
    optionRowKeys.current.set(option, next);
    return next;
  };

  const updateValidation = (key: 'maxLength' | 'min' | 'max', value: number | undefined) => {
    const validation = { ...(field.validation ?? {}) };
    if (value === undefined) delete validation[key];
    else validation[key] = value;
    onChange({ validation: Object.keys(validation).length > 0 ? validation : undefined });
  };
  const updateOptions = (nextOptions: Array<{ value: string; label?: string }>) => onChange({ config: { ...field.config, options: nextOptions } });
  const updateOption = (index: number, patch: Partial<{ value: string; label: string }>) => updateOptions(options.map((option, current) => {
    if (current !== index) return option;
    const updated = { ...option, ...patch };
    optionRowKeys.current.set(updated, optionRowKey(option));
    return updated;
  }));
  const moveOption = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= options.length) return;
    const next = [...options];
    [next[index], next[target]] = [next[target], next[index]];
    updateOptions(next);
  };
  const addOption = () => updateOptions([...options, { value: uniqueOptionValue(options), label: `선택 항목 ${options.length + 1}` }]);

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={1.25} sx={{ alignItems: 'center' }}>
        <FieldTypeIcon type={field.type} selected />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="subtitle2">필드 속성</Typography>
          <ValueBadge size="compact" label={workflowFieldTypeLabel(field.type)} />
        </Box>
      </Stack>

      {referenced && <Alert severity="info">기본 결재선이 이 구성원 필드를 사용합니다. 필드 키는 함께 반영되며, 유형 변경과 삭제는 결재선 설정을 바꾼 뒤 가능합니다.</Alert>}

      <FormTextField
        label={displayField ? '안내 내용' : '필드 이름'}
        value={field.label}
        required
        error={!field.label.trim()}
        helperText={!field.label.trim() ? '필드 이름을 입력하세요.' : undefined}
        help={displayField ? '요청 화면에서 입력란 대신 이 문구가 표시됩니다.' : undefined}
        onChange={(event) => onChange({ label: event.target.value })}
      />
      <FormTextField
        select
        label="필드 유형"
        value={field.type}
        disabled={referenced}
        helperText={referenced ? '결재선 승인자 기준으로 사용 중입니다.' : '유형을 바꾸면 기존 선택 항목과 검증 설정이 초기화될 수 있습니다.'}
        onChange={(event) => onTypeChange(event.target.value as WorkflowFieldType)}
      >
        {workflowFieldTypes.map((type) => <MenuItem key={type.value} value={type.value}><ValueBadge label={type.label} /></MenuItem>)}
      </FormTextField>
      {!displayField && <FormTextField label="도움말" value={field.helpText ?? ''} placeholder="예: 출장 목적과 방문지를 입력하세요." onChange={(event) => onChange({ helpText: event.target.value })} />}


      {choiceField && <Box>
        <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}><Typography variant="subtitle2">선택 항목</Typography><HelpPopover title="선택 항목">순서를 바꿔도 기존 저장 값은 유지됩니다.</HelpPopover></Stack>
        <Stack spacing={0.75} sx={{ mt: 1 }}>
          {options.map((option, index) => {
            const normalizedLabel = (option.label ?? '').trim().toLocaleLowerCase('ko-KR');
            const duplicateLabel = normalizedLabel !== '' && (optionLabelCounts.get(normalizedLabel) ?? 0) > 1;
            return <Paper key={optionRowKey(option)} variant="outlined" sx={{ p: 1, borderRadius: designTokens.radius.surface }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 1 }}>
                <FormTextField
                  size="small"
                  label={`항목 ${index + 1} 이름`}
                  value={option.label ?? ''}
                  required
                  error={!(option.label ?? '').trim() || duplicateLabel}
                  helperText={duplicateLabel ? '중복된 이름입니다.' : undefined}
                  onChange={(event) => updateOption(index, { label: event.target.value })}
                />

              </Box>
              <Stack direction="row" sx={{ mt: 0.5, justifyContent: 'flex-end' }}>
                <Tooltip title="위로 이동"><span><IconButton size="small" aria-label={`${index + 1}번 선택 항목 위로 이동`} disabled={index === 0} onClick={() => moveOption(index, -1)}><ArrowUpwardOutlined fontSize="small" /></IconButton></span></Tooltip>
                <Tooltip title="아래로 이동"><span><IconButton size="small" aria-label={`${index + 1}번 선택 항목 아래로 이동`} disabled={index === options.length - 1} onClick={() => moveOption(index, 1)}><ArrowDownwardOutlined fontSize="small" /></IconButton></span></Tooltip>
                <Tooltip title="삭제"><span><IconButton size="small" color="error" aria-label={`${index + 1}번 선택 항목 삭제`} disabled={options.length === 1} onClick={() => updateOptions(options.filter((_, current) => current !== index))}><DeleteOutlineOutlined fontSize="small" /></IconButton></span></Tooltip>
              </Stack>
            </Paper>;
          })}
        </Stack>
        <Button size="small" startIcon={<AddOutlined />} onClick={addOption} sx={{ mt: 0.75 }}>선택 항목 추가</Button>
      </Box>}

      {!displayField && <ChoiceControl kind="checkbox" checked={field.required} onCheckedChange={(checked) => onChange({ required: checked })} label="필수로 입력" />}
      <Accordion disableGutters expanded={advancedOpen || invalidKey || duplicateKey} onChange={(_, expanded) => setAdvancedOpen(expanded)}>
        <AccordionSummary expandIcon={<ExpandMoreOutlined />}><Typography variant="body2">필드 세부 설정</Typography></AccordionSummary>
        <AccordionDetails><Stack spacing={2}>
      <FormTextField
        label="필드 키"
        value={field.key}
        required
        error={invalidKey || duplicateKey}
        helperText={duplicateKey ? '같은 필드 키가 이미 있습니다.' : invalidKey ? '영문 소문자로 시작하고 소문자, 숫자, 밑줄만 사용하세요.' : referenced ? '키를 바꾸면 이 필드를 참조하는 결재선도 함께 변경됩니다.' : undefined}
        help="API와 내보내기에서 사용하는 고유한 이름입니다."
        onChange={(event) => onKeyChange(normalizeFieldKey(event.target.value))}
      />
          {choiceField && <Stack spacing={1}>
            <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}><Typography variant="subtitle2">선택 항목 저장 값</Typography><HelpPopover title="선택 항목 저장 값">기존 응답과 연동에서 사용하는 값입니다. 표시 이름을 바꿔도 유지됩니다.</HelpPopover></Stack>
            {options.map((option) => <FormTextField key={optionRowKey(option)} label="저장 값" value={option.value} required
              error={!option.value.trim() || (optionValueCounts.get(option.value) ?? 0) > 1}
              helperText={(optionValueCounts.get(option.value) ?? 0) > 1 ? '중복된 값입니다.' : option.label}
              onChange={(event) => updateOption(options.indexOf(option), { value: normalizeOptionValue(event.target.value) })} />)}
          </Stack>}
      {!displayField && <>
        {textField && <FormTextField
          type="number"
          label="최대 글자 수"
          value={maximumLength ?? ''}
          error={maximumLength !== undefined && (!Number.isInteger(maximumLength) || Number(maximumLength) < 1)}
          help="비워두면 글자 수를 제한하지 않습니다."
          slotProps={{ htmlInput: { min: 1, step: 1 } }}
          onChange={(event) => updateValidation('maxLength', event.target.value === '' ? undefined : Number(event.target.value))}
        />}
        {numberField && <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
          <FormTextField
            type="number"
            label="최솟값"
            value={minimum ?? ''}
            error={invalidRange}
            helperText={invalidRange ? '최댓값보다 작아야 합니다.' : undefined}
            onChange={(event) => updateValidation('min', event.target.value === '' ? undefined : Number(event.target.value))}
          />
          <FormTextField
            type="number"
            label="최댓값"
            value={maximum ?? ''}
            error={invalidRange}
            helperText={invalidRange ? '최솟값보다 커야 합니다.' : undefined}
            onChange={(event) => updateValidation('max', event.target.value === '' ? undefined : Number(event.target.value))}
          />
        </Box>}
        {fileField && <FormTextField
          type="number"
          label="최대 파일 수"
          value={Number(field.config?.maxFiles ?? 10)}
          helperText="한 필드에 1개 이상 20개 이하로 등록할 수 있습니다."
          slotProps={{ htmlInput: { min: 1, max: 20, step: 1 } }}
          onChange={(event) => onChange({ config: { ...(field.config ?? {}), maxFiles: Number(event.target.value) } })}
        />}
        <FormTextField select label="민감도" value={field.sensitivity} onChange={(event) => onChange({ sensitivity: event.target.value as WorkflowFieldDefinition['sensitivity'], searchable: event.target.value === 'INTERNAL' ? field.searchable : false })}>
          <MenuItem value="INTERNAL"><ValueBadge label="일반" /></MenuItem>
          <MenuItem value="CONFIDENTIAL"><ValueBadge label="기밀" /></MenuItem>
          <MenuItem value="RESTRICTED"><ValueBadge label="제한" /></MenuItem>
        </FormTextField>
        <Stack spacing={0.25}>
          <ChoiceControl kind="checkbox" checked={field.searchable} disabled={field.sensitivity !== 'INTERNAL'} onCheckedChange={(checked) => onChange({ searchable: checked })} label="요청 검색에 사용" />
          <ChoiceControl kind="checkbox" checked={field.exportable} onCheckedChange={(checked) => onChange({ exportable: checked })} label="Excel 내보내기에 포함" />
        </Stack>
      </>}
        </Stack></AccordionDetails>
      </Accordion>

      <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
        <Button variant="text" startIcon={<ContentPasteOutlined />} onClick={onDuplicate}>필드 복제</Button>
        <Button color="error" variant="text" startIcon={<DeleteOutlineOutlined />} disabled={referenced} onClick={onDelete}>필드 삭제</Button>
      </Stack>
    </Stack>
  );
}

function FieldTypePicker({ anchor, titleId, onClose, onSelect }: { anchor: HTMLElement | null; titleId: string; onClose: () => void; onSelect: (type: WorkflowFieldType) => void }) {
  const groups = useMemo(() => ['입력', '선택·참조', '안내'] as const, []);
  return (
    <AnchoredPopover
      anchorEl={anchor}
      onClose={onClose}
      placement="bottom-end"
      size="standard"
      ariaLabelledBy={titleId}
    >
      <Box sx={{ minWidth: 0 }}>
        <Box sx={{ px: 2, pt: 2, pb: 1.25 }}>
          <Typography id={titleId} variant="h6">필드 유형 선택</Typography>
          <Typography variant="body2" color="text.secondary">요청자에게 받을 정보의 형식을 고르세요.</Typography>
        </Box>
        <Divider />
        <Stack sx={{ p: 1, overflowY: 'auto' }} spacing={1}>
          {groups.map((group) => <Box key={group}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 1, pt: 0.5, pb: 0.4, fontWeight: 700 }}>{group}</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 0.5 }}>
              {workflowFieldTypes.filter((type) => type.group === group).map((type) => <ButtonBase
                key={type.value}
                onClick={() => onSelect(type.value)}
                sx={{
                  minWidth: 0,
                  p: 1,
                  borderRadius: designTokens.radius.menuItem,
                  alignItems: 'flex-start',
                  justifyContent: 'flex-start',
                  textAlign: 'left',
                  gap: 1,
                  '&:hover': { bgcolor: designTokens.color.hoverSurface },
                  '&.Mui-focusVisible': createFocusRingStyles({ inset: true }),
                }}
              >
                <FieldTypeIcon type={type.value} />
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>{type.label}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.4 }}>{type.description}</Typography>
                </Box>
              </ButtonBase>)}
            </Box>
          </Box>)}
        </Stack>
      </Box>
    </AnchoredPopover>
  );
}

function FieldTypeIcon({ type, selected = false }: { type: WorkflowFieldType; selected?: boolean }) {
  const option = workflowFieldTypes.find((candidate) => candidate.value === type);
  return <Box sx={{ width: 36, height: 36, flex: '0 0 auto', borderRadius: designTokens.radius.surface, display: 'grid', placeItems: 'center', bgcolor: selected ? designTokens.color.brandSky : designTokens.color.subtleSurface, color: selected ? designTokens.color.action : designTokens.color.secondaryText, '& svg': { fontSize: 20 } }}>{option?.icon}</Box>;
}

function createField(fields: WorkflowFieldDefinition[], type: WorkflowFieldType): WorkflowFieldDefinition {
  const option = workflowFieldTypes.find((candidate) => candidate.value === type) ?? workflowFieldTypes[0];
  const choiceField = type === 'SINGLE_SELECT' || type === 'MULTI_SELECT';
  return {
    key: uniqueFieldKey(fields, option.defaultKey),
    order: fields.length + 1,
    type,
    label: option.defaultLabel,
    required: false,
    sensitivity: 'INTERNAL',
    searchable: false,
    exportable: type !== 'DISPLAY_TEXT',
    config: choiceField ? { options: [{ value: 'option_1', label: '선택 항목 1' }] } : type === 'FILE' ? { maxFiles: 10 } : undefined,
  };
}

function normalizeFieldType(field: WorkflowFieldDefinition, type: WorkflowFieldType): Partial<WorkflowFieldDefinition> {
  const choiceField = type === 'SINGLE_SELECT' || type === 'MULTI_SELECT';
  const existingOptions = fieldOptions(field);
  return {
    type,
    required: type === 'DISPLAY_TEXT' ? false : field.required,
    searchable: type === 'DISPLAY_TEXT' ? false : field.searchable,
    exportable: type === 'DISPLAY_TEXT' ? false : field.exportable,
    config: choiceField
      ? { options: existingOptions.length > 0 ? existingOptions : [{ value: 'option_1', label: '선택 항목 1' }] }
      : type === 'FILE' ? { maxFiles: 10 } : undefined,
    validation: undefined,
  };
}

function reorderFields(fields: WorkflowFieldDefinition[]): WorkflowFieldDefinition[] {
  return fields.map((field, index) => ({ ...field, order: index + 1 }));
}

function uniqueFieldKey(fields: WorkflowFieldDefinition[], base: string): string {
  const keys = new Set(fields.map((field) => field.key));
  const trimmedBase = base.slice(0, 64);
  if (!keys.has(trimmedBase)) return trimmedBase;
  let suffix = 2;
  let candidate = '';
  do {
    const suffixText = `_${suffix}`;
    candidate = `${trimmedBase.slice(0, 64 - suffixText.length)}${suffixText}`;
    suffix += 1;
  } while (keys.has(candidate));
  return candidate;
}

function normalizeFieldKey(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9_]/g, '_').slice(0, 64);
}

function fieldOptions(field: WorkflowFieldDefinition): Array<{ value: string; label?: string }> {
  return Array.isArray(field.config?.options) ? field.config.options as Array<{ value: string; label?: string }> : [];
}

function normalizeOptionValue(value: string): string {
  return value.trim().toLowerCase().replace(/[^a-z0-9가-힣]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 64);
}

function uniqueOptionValue(options: Array<{ value: string }>): string {
  const values = new Set(options.map((option) => option.value));
  let index = options.length + 1;
  while (values.has(`option_${index}`)) index += 1;
  return `option_${index}`;
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function sensitivityLabel(value: WorkflowFieldDefinition['sensitivity']): string {
  return ({ INTERNAL: '일반', CONFIDENTIAL: '기밀', RESTRICTED: '제한' } as const)[value];
}
